// 홈택스 엑셀 쓰기·읽기 (C420). 백엔드는 엑셀을 다루지 않으므로 프론트가 소유한다.
//
// 정본: docs/invoice-prd/C420_세금계산서업로드생성_검증작업_v1.html

import type { NtsRow } from '@/lib/api/data-contracts';
import * as XLSX from 'xlsx';

/**
 * 국세청 홈택스 "엑셀 업로드 양식(전자세금계산서-일반(영세율))" 파일의 1~5행 주의사항.
 * 6행이 컬럼 타이틀, 7행부터 데이터다. 5행은 원본 양식의 빈 줄로, 타이틀 행이 정확히
 * 6번째 줄에 오게 한다. 서버 응답에 없으므로 프론트 상수로 둔다.
 */
export const UPLOAD_NOTICE_ROWS: string[][] = [
  ['엑셀 업로드 양식(전자세금계산서-일반(영세율))'],
  [
    '★주황색으로 표시된 부분은 필수입력항목으로 반드시 입력하셔야 합니다.\n' +
      "★아래 '항목설명' 시트를 참고하여 작성하시기 바랍니다.",
  ],
  [
    '★실제 업로드할 DATA는 7행부터 입력하여야 합니다. 최대 100건까지 입력이 가능하나, ' +
      '발급은 최대 10건씩 처리가 됩니다.(100건 초과 자료는 처리 안됨)\n' +
      '★임의로 행을 추가하거나 삭제하는 경우 파일을 제대로 읽지 못하는 경우가 있으므로, ' +
      '주어진 양식안에 반드시 작성을 하시기 바랍니다.',
  ],
  [
    '★전자(세금)계산서 종류는 엑셀 업로드 양식에 따라 해당 전자(세금)계산서 종류코드를 ' +
      '반드시 입력하셔야 합니다.\n★품목은 1건이상 입력해야 합니다.\n' +
      "★공급받는자 등록번호는 사업자등록번호, 주민등록번호를 입력할 수 있습니다. 외국인인 경우 " +
      "'9999999999999'를 입력하시고, 비고란에 외국인등록번호 또는 여권번호를 입력하시기 바랍니다.",
  ],
  [],
];

const UPLOAD_SHEET_NAME = '엑셀업로드양식';
const NTS_SHEET_NAME = '세금계산서';

/**
 * 매출전자세금계산서목록 파일의 문서 컬럼 위치. 홈택스 원본 시트의 컬럼 순서와 같아야 한다.
 * 이름이 겹치는 컬럼(종사업장번호·상호 등)이 있어 헤더명이 아니라 위치로 읽는다.
 * 품목 컬럼은 파일마다 위치가 달라 {@link resolveItemCols}로 헤더에서 찾는다.
 */
const NTS_COL = {
  wdate: 0,
  apprNo: 1,
  issueDt: 2,
  sendDt: 3,
  supEnt: 4,
  buyEnt: 9,
  buyName: 11,
  total: 14,
  supply: 15,
  vat: 16,
  category: 17,
  remark: 20,
  purpose: 21,
} as const;

/**
 * 품목 컬럼의 위치를 헤더에서 찾는다. 품목 블록만 파일마다 위치가 달라 고정할 수 없다.
 *
 * <p>문서 공급가액(15)과 이름이 겹치므로 <b>마지막</b> '공급가액'을 품목공급가액으로 잡고 나머지
 * 7개를 상대 위치로 푼다. 못 찾으면 엉뚱한 칸을 등록하느니 파일을 거부한다.
 */
export function resolveItemCols(headers: string[]) {
  // findLastIndex는 tsconfig lib이 ES2020이라 타입이 없다
  const supply = headers.map(header => header.includes('공급가액')).lastIndexOf(true);
  if (supply <= NTS_COL.supply) {
    throw new Error(
      '매출전자세금계산서목록 파일에서 품목공급가액 컬럼을 찾지 못했습니다 (파일 서식을 확인하세요)'
    );
  }
  return {
    itemDate: supply - 5,
    itemName: supply - 4,
    itemSpec: supply - 3,
    itemQty: supply - 2,
    itemUnitPrice: supply - 1,
    itemSupply: supply,
    itemVat: supply + 1,
    itemRemark: supply + 2,
  };
}

type SheetCell = string | number | null | undefined;

/**
 * SheetJS를 raw:false로 읽으면 금액이 "14,136,525" 문자열로 나온다.
 * 서버 계약은 JSON 숫자이므로 콤마를 제거해 숫자로 만든다.
 */
export function parseAmt(value: SheetCell): number | undefined {
  if (value === '' || value === null || value === undefined) {
    return undefined;
  }
  const parsed = Number(String(value).replace(/,/g, ''));
  return Number.isNaN(parsed) ? undefined : parsed;
}

/** 서버 계약이 LocalDate이므로 '2026.07.31' · '20260731' 같은 표기도 ISO로 맞춘다 */
export function toIsoDate(value: SheetCell): string | undefined {
  if (value === '' || value === null || value === undefined) {
    return undefined;
  }
  const text = String(value).trim();
  const compact = text.replace(/[.\-/]/g, '');
  if (!/^\d{8}$/.test(compact)) {
    return undefined;
  }
  return `${compact.slice(0, 4)}-${compact.slice(4, 6)}-${compact.slice(6, 8)}`;
}

function text(value: SheetCell): string {
  return value === null || value === undefined ? '' : String(value);
}

/**
 * 업로드 양식 파일을 만든다. 컬럼 타이틀과 행은 서버(GET tax-invoices/upload-rows)가 준
 * 59컬럼을 그대로 쓰고, 프론트는 주의사항 5행과 병합만 얹는다.
 */
export function buildUploadWorkbook(columns: string[], rows: unknown[][]): Blob {
  const sheet = XLSX.utils.aoa_to_sheet([...UPLOAD_NOTICE_ROWS, columns, ...rows]);

  // 실제 양식처럼 주의사항 행을 컬럼 폭 전체에 걸쳐 병합한다
  sheet['!merges'] = UPLOAD_NOTICE_ROWS.flatMap((row, index) =>
    row.length > 0
      ? [{ s: { r: index, c: 0 }, e: { r: index, c: columns.length - 1 } }]
      : []
  );

  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, UPLOAD_SHEET_NAME);
  const buffer = XLSX.write(book, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

interface SaveFilePickerWindow {
  showSaveFilePicker?: (options: {
    suggestedName: string;
    types: { description: string; accept: Record<string, string[]> }[];
  }) => Promise<{
    createWritable: () => Promise<{
      write: (data: Blob) => Promise<void>;
      close: () => Promise<void>;
    }>;
  }>;
}

/**
 * 업로드 양식을 저장하고 성공 여부를 돌려준다.
 *
 * <p>생성횟수는 되돌릴 수 없고 생성취소를 막으므로 저장을 확인한 뒤에만 올려야 한다.
 * 저장 위치 선택 API를 쓸 수 있으면 쓰기 완료를 그대로 성공으로 보고, 없으면
 * 내려받기 후 사용자에게 저장 여부를 직접 확인받는다.
 */
export async function saveWorkbook(blob: Blob, fileName: string): Promise<boolean> {
  const picker = (window as SaveFilePickerWindow).showSaveFilePicker;
  if (picker) {
    try {
      const handle = await picker({
        suggestedName: fileName,
        types: [
          {
            description: 'Excel',
            accept: {
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
                '.xlsx',
              ],
            },
          },
        ],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return true;
    } catch {
      // 사용자가 저장을 취소했거나 쓰기에 실패한 경우
      return false;
    }
  }

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  // click 직후 해제하면 내려받기가 끊길 수 있어 다음 태스크로 미룬다
  setTimeout(() => URL.revokeObjectURL(url), 0);
  return window.confirm(
    `${fileName} 파일이 저장됐습니까?\n확인을 누르면 생성횟수가 올라가고 생성취소가 막힙니다.`
  );
}

/**
 * 집계행 판정 키워드. PRD §5.8이 정한 목록이며 서버(HometaxMatcher.AGGREGATE_KEYWORDS)와 같아야 한다.
 */
const AGGREGATE_KEYWORDS = ['합계', '소계', '총계', '합 계', '총건수', '건수합계', '평균'];

/**
 * 집계행인지 판정한다 - 작성일자나 승인번호가 비었거나, 어떤 셀이든 합계성 문구를 포함하는 행.
 *
 * <p>PRD §5.8과 정본 프로토타입의 isAggregateNtsRow가 같은 기준이다.
 */
function isAggregateRow(row: SheetCell[]): boolean {
  if (!text(row[NTS_COL.wdate]) || !text(row[NTS_COL.apprNo])) {
    return true;
  }
  return row.some(cell => AGGREGATE_KEYWORDS.some(keyword => text(cell).includes(keyword)));
}

/** 파일을 2차원 셀 배열로 읽는다. 원하는 시트가 없으면 첫 시트. 빈 행도 그대로 둔다(header:1 기본) */
async function readAoa(file: File, preferredSheet: string): Promise<SheetCell[][]> {
  const book = XLSX.read(new Uint8Array(await file.arrayBuffer()), { type: 'array' });
  const sheetName = book.SheetNames.includes(preferredSheet) ? preferredSheet : book.SheetNames[0];
  return XLSX.utils.sheet_to_json<SheetCell[]>(book.Sheets[sheetName], { header: 1, raw: false, defval: '' });
}

/** 국세청 목록 파싱 결과. 제외된 집계행 수를 함께 돌려 PRD §5.8의 안내를 화면이 띄울 수 있게 한다. */
export interface NtsListParseResult {
  rows: NtsRow[];
  /** 집계행으로 판정해 제외한 행 수 */
  excludedCount: number;
  /** 파일의 컬럼 타이틀 행 - 우측 패널은 원본 그대로 보여준다(정본 NTS_COLS) */
  headers: string[];
  /** rows와 같은 순서의 원본 셀 값 */
  rawRows: string[][];
}

/**
 * 국세청 매출전자세금계산서목록 파일을 읽어 NtsRow로 정규화한다.
 *
 * <p>원본 파일은 앞쪽에 제목·안내 행이 섞여 있을 수 있어 1행이 컬럼 타이틀이라고 가정할 수 없다.
 * '작성일자'·'승인번호'·'발급일자'가 모두 있는 행을 타이틀로 찾고 그 다음 행부터 데이터로 읽는다.
 *
 * <p>집계행은 여기서 걸러내고 제외 건수를 함께 돌려준다(PRD §5.8 - 파일 로드 직후 안내).
 * 서버도 같은 기준으로 한 번 더 거르므로, 이 필터가 놓쳐도 매칭에는 섞이지 않는다.
 */
export async function parseNtsListFile(file: File): Promise<NtsListParseResult> {
  const aoa = await readAoa(file, NTS_SHEET_NAME);

  const headerIndex = aoa.findIndex(row => {
    const joined = row.map(cell => text(cell)).join('|');
    return (
      joined.includes('작성일자') &&
      joined.includes('승인번호') &&
      joined.includes('발급일자')
    );
  });
  // 대사는 판정이 대사완료가 아닌 문서의 승인번호를 지우므로, 다른 파일을 골랐을 때
  // 전부 미대사로 판정돼 승인번호가 날아가지 않도록 여기서 막는다.
  if (headerIndex < 0) {
    throw new Error(
      '매출전자세금계산서목록 파일이 아닙니다 (작성일자·승인번호·발급일자 컬럼을 찾지 못했습니다)'
    );
  }

  const filledRows = aoa
    .slice(headerIndex + 1)
    .filter(row => row.some(cell => cell !== '' && cell !== null && cell !== undefined));
  if (filledRows.length === 0) {
    throw new Error('파일에서 읽을 수 있는 자료가 없습니다');
  }

  const dataRows = filledRows.filter(row => !isAggregateRow(row));
  const excludedCount = filledRows.length - dataRows.length;
  const headers = aoa[headerIndex].map(cell => text(cell));
  const itemCol = resolveItemCols(headers);

  const rows = dataRows.map(row => ({
    wdate: toIsoDate(row[NTS_COL.wdate]),
    apprNo: text(row[NTS_COL.apprNo]),
    issueDt: toIsoDate(row[NTS_COL.issueDt]),
    sendDt: toIsoDate(row[NTS_COL.sendDt]),
    supEnt: text(row[NTS_COL.supEnt]),
    buyEnt: text(row[NTS_COL.buyEnt]),
    buyName: text(row[NTS_COL.buyName]),
    total: parseAmt(row[NTS_COL.total]),
    supply: parseAmt(row[NTS_COL.supply]),
    vat: parseAmt(row[NTS_COL.vat]),
    category: text(row[NTS_COL.category]),
    remark: text(row[NTS_COL.remark]),
    purpose: text(row[NTS_COL.purpose]),
    itemDate: text(row[itemCol.itemDate]),
    itemName: text(row[itemCol.itemName]),
    itemSpec: text(row[itemCol.itemSpec]),
    itemQty: parseAmt(row[itemCol.itemQty]),
    itemUnitPrice: parseAmt(row[itemCol.itemUnitPrice]),
    itemSupply: parseAmt(row[itemCol.itemSupply]),
    itemVat: parseAmt(row[itemCol.itemVat]),
    itemRemark: text(row[itemCol.itemRemark]),
  }));

  // 집계행을 걷어내고 남는 문서 행이 없으면 서버 입장에서는 빈 자료로 대사하는 것과 같아
  // 선택한 문서가 전부 미대사가 되고 저장된 승인번호까지 지워진다. 여기서 막는다.
  if (rows.length === 0) {
    throw new Error(
      '작성일자와 승인번호가 있는 문서 행이 없습니다 (파일 서식을 확인하세요)'
    );
  }
  return { rows, excludedCount, headers, rawRows: dataRows.map(row => row.map(cell => text(cell))) };
}

/**
 * 저장해 둔 업로드 생성자료(세금계산서등록양식) 파일을 되읽는다(C420 PRD §5).
 *
 * <p>양식은 1~5행 주의사항, 6행 컬럼 타이틀, 7행부터 데이터로 고정이라 위치 기준으로 읽는다.
 */
export async function parseUploadTemplateFile(
  file: File
): Promise<{ columns: string[]; rows: string[][] }> {
  const aoa = await readAoa(file, UPLOAD_SHEET_NAME);
  const header = aoa[UPLOAD_NOTICE_ROWS.length] ?? [];
  const columns = header.map(cell => text(cell));
  if (!columns.some(c => c.includes('작성일자')) || !columns.some(c => c.includes('공급가액'))) {
    throw new Error('세금계산서등록양식 파일이 아닙니다 (6행에서 컬럼 타이틀을 찾지 못했습니다)');
  }
  const rows = aoa
    .slice(UPLOAD_NOTICE_ROWS.length + 1)
    .filter(row => row.some(cell => text(cell) !== ''))
    .map(row => columns.map((_, i) => text(row[i])));
  return { columns, rows };
}
